import { useToggleFavorite } from "@/api/resourceTags";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionCardMenu,
  buildFileRecordKind,
  useResolvedFileRecordActions,
  type ActionScope,
} from "@/object-actions";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import { RESOURCE_TAG_FAVORITE, type FileRecord } from "@cohesive/api-client";
import { WorkspaceFileCard } from "@cohesive/ui-components";
import { useMemo, type ReactNode } from "react";

export interface WorkspaceFileCardItemProps {
  record: FileRecord;
  isSelected: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>;
  thumbnailWrapper?: (thumb: ReactNode) => ReactNode;
  /** Imperative callbacks the registry handlers wrap (open/rename/move/copy/delete). */
  handlerDeps: FileRecordActionHandlerDeps;
  scope?: ActionScope;
}

/**
 * Registry-backed `WorkspaceFileCard`. Owns one `useRailRowContextMenu`
 * controller so the card's kebab and right-click open the same manifest-driven
 * menu (same actions as the rail/list views). The corner star stays imperative,
 * wired to the favorites mutation like the previous card.
 */
export function WorkspaceFileCardItem({
  record,
  isSelected,
  onClick,
  onDoubleClick,
  thumbnailWrapper,
  handlerDeps,
  scope = "canonical",
}: WorkspaceFileCardItemProps) {
  const controller = useRailRowContextMenu();
  const toggleFavorite = useToggleFavorite();

  const isFavorite = !!record.tags?.includes(RESOURCE_TAG_FAVORITE);

  const kind = useMemo(() => buildFileRecordKind(record), [record]);

  const { actions } = useResolvedFileRecordActions({
    record,
    surface: "main-view",
    enabled: controller.open,
    scope,
    handlerDeps: {
      ...handlerDeps,
      actionScope: scope,
    },
  });

  return (
    <WorkspaceFileCard
      record={record}
      isSelected={isSelected}
      isFavorite={isFavorite}
      onToggleStar={() =>
        toggleFavorite.mutate({
          resourceType: "file-record",
          resourceId: record.id,
          isFavorite,
        })
      }
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={controller.onContextMenu}
      thumbnailWrapper={thumbnailWrapper}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={kind}
          name={record.path?.split("/").filter(Boolean).pop() || "File"}
        />
      }
    />
  );
}
